/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { TeamOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  isDraftTeam,
  isTeamRosterFull,
  startTeamImageUrl,
  teamRosterLabel,
  TEAM_FULL_MESSAGE,
} from '@sportninja/common/features/registration/startStep/teamDisplay'
import type { StartTeam } from '@sportninja/common/features/registration/startStep/types'
import { BUCKET_IMAGE_SRC } from 'src/components/ts-components/icons/bucketImage'
import Text from 'src/components/ts-components/Text'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface Props {
  team: StartTeam
  /** The division this team sits in, named so the reader sees what it implies. */
  divisionName?: string | null
  isSelected: boolean
  /** Dims the unpicked cards once a choice is made, matching the division grid. */
  hasSelection: boolean
  onSelect: (team: StartTeam) => void
}

/**
 * One team in the Browse Teams grid.
 *
 * Deliberately the same anatomy as `DivisionCard` — a titled card with a
 * details row and a bordered, selectable frame — so the two tabs read as two
 * views of one question rather than two different screens.
 *
 * Everything past the name is optional. The trimmed team objects nested in the
 * registration payload carry no roster data, so those cards simply show less
 * rather than showing zeros.
 */
const TeamCard = ({
  team,
  divisionName,
  isSelected,
  hasSelection,
  onSelect,
}: Props) => {
  const isDraft = isDraftTeam(team)
  const roster = teamRosterLabel(team)
  const isFull = isTeamRosterFull(team)
  const logo = resolveAssetUrl(startTeamImageUrl(team))

  const card = (
    <button
      type='button'
      // A full roster cannot take another registration, so the card states that
      // rather than accepting a click the next step would have to reject.
      disabled={isFull}
      css={[
        cardStyle,
        isSelected && cardSelectedStyle,
        hasSelection && !isSelected && cardDimmedStyle,
        isFull && cardFullStyle,
      ]}
      onClick={() => onSelect(team)}
      aria-pressed={isSelected}
    >
      <div css={headerStyle}>
        {/* A draft has no crest of its own, so it gets the shared bucket image
            — the same one the rest of the wizard uses for a bucket, and
            tenant-aware, rather than a second draft icon invented here. */}
        <img
          css={avatarStyle}
          src={isDraft ? BUCKET_IMAGE_SRC : logo || TEAM_PLACEHOLDER}
          alt=''
        />

        <span css={titleStyle}>
          <Text variant='heading6' weight='semiBold' color={colors.WHITE}>
            {team.name ?? 'Unnamed Team'}
          </Text>
          {divisionName ? (
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              {divisionName}
            </Text>
          ) : null}
        </span>
      </div>

      {isDraft || roster ? (
        <>
          <span css={dividerStyle} />
          <div css={detailsStyle}>
            {isDraft ? (
              <span css={badgeStyle}>
                <Text
                  variant='paragraphXSmall'
                  weight='semiBold'
                  color={colors.PRIMARY}
                >
                  Draft
                </Text>
              </span>
            ) : null}
            {roster ? (
              <span css={rosterStyle}>
                <TeamOutlined css={rosterIconStyle} />
                <Text
                  variant='paragraphXSmall'
                  weight='regular'
                  color={isFull ? colors.ERROR_200 : colors.NEUTRAL_50}
                >
                  {roster}
                </Text>
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </button>
  )

  if (!isFull) {
    return card
  }

  // A disabled button emits no pointer events, so the tooltip cannot listen on
  // it directly — the span is what the reader actually hovers.
  return (
    <Tooltip title={TEAM_FULL_MESSAGE}>
      <span css={fullWrapperStyle}>{card}</span>
    </Tooltip>
  )
}

export default TeamCard

const cardStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  height: 100%;
  min-height: 110px;
  padding: 16px;
  border: 1px solid ${colors.NEUTRAL_500};
  border-radius: 16px;
  background-color: transparent;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease-in-out, opacity 0.15s ease-in-out;

  &:hover {
    border-color: ${colors.PRIMARY};
  }
`

const cardSelectedStyle = css`
  border: 2px solid ${colors.PRIMARY};
  background-color: ${colors.NEUTRAL_700};
`

const cardDimmedStyle = css`
  opacity: 0.5;
`

/* A full team reads as unavailable rather than merely unpicked: the hover
   affordance goes with the ability to act on it. Dimmed less than
   `cardDimmedStyle` so the roster count stays readable — it is the reason the
   card is disabled. */
const cardFullStyle = css`
  opacity: 0.6;
  cursor: not-allowed;
  &:hover {
    border-color: ${colors.NEUTRAL_500};
  }
`

/* The tooltip's hover target. A disabled button emits no pointer events, so the
   wrapper is what receives them; it fills the grid cell so the whole card is
   hoverable, not just the strip around it. */
const fullWrapperStyle = css`
  display: block;
  width: 100%;
  height: 100%;
  cursor: not-allowed;
`

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-width: 0;
`

const avatarStyle = css`
  width: 40px;
  height: 40px;
  flex: none;
  border-radius: 8px;
  object-fit: contain;
  background-color: ${colors.NEUTRAL_700};
`

const titleStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const dividerStyle = css`
  width: 100%;
  height: 1px;
  background-color: ${colors.NEUTRAL_500};
`

const detailsStyle = css`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`

const badgeStyle = css`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
`

const rosterStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

/* antd icons render as glyphs, so font-size IS their size control — this is
   icon sizing, not type. Text in this feature uses the css.js presets. */
const rosterIconStyle = css`
  color: ${colors.NEUTRAL_50};
  font-size: 14px;
`
